import { useMemo } from 'react';

import { useDatafeedSearchCauseStores } from '@givebacks/api/datafeed';
import {
  CauseImage,
  StoreItem,
  StoreItemsResponse,
  useListSelectedCausesInfo,
  useMakeListStoreItemsQueryObject,
} from '@givebacks/api/store';
import { Native } from '@givebacks/utils';
import { useQueries } from '@tanstack/react-query';
import { useBreakPoints, usePlatformService } from 'core';
import { MyFavoriteCarouselCard, Search } from 'core/Components';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CauseDetail,
  CauseStoreCard,
  ConditionalWrapper,
  Container,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
  Transition,
  centsToDollar,
} from 'ui';

import classes from './MyStores.module.css';

const NUM_ITEMS_PER_STORE = 4;

const useSearchCauseStores = (searchTerm?: string) => {
  const { data: causes, isLoading } = useDatafeedSearchCauseStores({
    search: searchTerm ? encodeURIComponent(searchTerm) : undefined,
  });

  return { data: causes ?? [], isLoading: isLoading && !!searchTerm };
};

const MyStoresSearch = () => {
  const { isSmallScreen } = useBreakPoints();
  const navigate = useNavigate();

  return (
    <ConditionalWrapper
      condition={!isSmallScreen}
      wrapper={(children) => (
        <Stack gap="lg" className={classes.MyStores__searchContainer}>
          <Title className={classes.MyStores__searchContainerTitle} order={2}>
            Find Cause Stores
          </Title>

          {children}
        </Stack>
      )}
    >
      <Search
        className={classes.MyStores__search}
        useSearchFn={useSearchCauseStores}
        placeholder="Search by cause or keyword"
        optionKey={(cause) => cause.uuid}
        optionValue={(cause) => cause.uuid}
        renderOption={(cause) => (
          <CauseStoreCard
            className={classes.MyStores__card}
            key={cause.uuid}
            direction="column"
            imgSrc={cause.thumbnail}
            title={cause.name}
            info={{
              detail: (
                <CauseDetail city={cause?.city} state={cause?.state} website={cause?.website} />
              ),
            }}
            w="100%"
          />
        )}
        onOptionSubmit={(val) => navigate(`../${val}`)}
      />
    </ConditionalWrapper>
  );
};

const MyStoresHeader = () => {
  const { isSmallScreen } = useBreakPoints();
  const Platform = usePlatformService();

  const url = 'https://support.givebacks.com/hc/en-us/sections/24998345637527';

  return (
    <Group gap="sm" justify="space-between">
      <Title order={2}>My Favorite Stores</Title>
      <Button
        color="cobalt"
        size={isSmallScreen ? 'xs' : 'sm'}
        variant="subtle"
        component={'a'}
        href={url}
        target="_blank"
        onClick={(e) => {
          if (Platform.isNative) {
            e.preventDefault();
            Native.postURL(url);
          }
        }}
      >
        Learn More
      </Button>
    </Group>
  );
};

const MyFavoriteStoreCarouselCard = ({
  cause,
  storeItemData,
}: {
  cause: {
    name: string;
    uuid: string;
    banner?: CauseImage;
    logo?: CauseImage;
    store_active: boolean;
    shortener?: string;
  };
  storeItemData?: StoreItemsResponse<StoreItem[]>;
}) => {
  const navigate = useNavigate();

  const prettyCauseUrl = useMemo(() => {
    return cause.shortener ? cause.shortener : cause.uuid;
  }, [cause]);

  const carouselItems = useMemo(() => {
    if (!storeItemData) return [];

    return storeItemData.store_items.map((item) => {
      return {
        name: item.name,
        subtitle: (
          <Text fw="600" fz="10" c="mint">
            {centsToDollar(item.price)}
          </Text>
        ),
        image: item.images[0]?.url ?? '',
        onClick: () => navigate(`/shop/stores/${prettyCauseUrl}/items/${item.uuid}`),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- no need to put `navigate` in deps
  }, [storeItemData]);

  return (
    <MyFavoriteCarouselCard
      key={cause.uuid}
      logo={cause?.logo?.url}
      title={cause?.name}
      items={carouselItems}
      menuItems={[
        {
          label: 'Visit Cause Store',
          onClick: () => navigate(`/shop/stores/${prettyCauseUrl}`),
        },
        {
          label: 'Visit Cause Page',
          onClick: () => navigate(`/causes/${prettyCauseUrl}`),
        },
      ]}
      subtitle={`${storeItemData?.meta.total} product${storeItemData?.meta.total === 1 ? '' : 's'}`}
      numTotalItems={storeItemData?.meta.total}
      maxVisibleItems={NUM_ITEMS_PER_STORE}
      onViewAllClick={() => navigate(`/shop/stores/${prettyCauseUrl}`)}
      aspectRatio={3 / 2}
    />
  );
};

const MyStores = () => {
  const { data: userCauseSelections, isLoading: isLoadingUserCauseSelections } =
    useListSelectedCausesInfo();

  const { makeListStoreItemsQueryObject } = useMakeListStoreItemsQueryObject({
    queryOptions: {
      order: {
        field: 'order',
        direction: 'asc',
      },
      join: 'AND',
      searches: {
        type: {
          value: 'StoreItems::Item,StoreItems::Membership',
          by: 'in',
        },
        in_store: {
          value: true,
        },
        live: {
          value: true,
        },
      },
      options: {
        total: true,
      },
    },
    pagination: {
      limit: NUM_ITEMS_PER_STORE,
    },
    _enabled: !!userCauseSelections,
  });

  const stores = useQueries({
    queries: userCauseSelections?.map((cause) => makeListStoreItemsQueryObject(cause.uuid)) ?? [],
  });

  const isLoading = useMemo(
    () => stores.some((store) => store.isLoading) || isLoadingUserCauseSelections,
    [stores, isLoadingUserCauseSelections]
  );

  const causeCards = useMemo(() => {
    if (!userCauseSelections || stores.every((store) => store.data?.meta.total === 0)) {
      return (
        <Card bg="gray.0" bd="1px solid gray.1">
          <Stack gap="4">
            <Text c="gray" fw="600" fz="xs">
              Put your favorites at your fingertips
            </Text>
            <Text c="gray" fz="10">
              It looks like you haven&apos;t added any favorites yet. You can tap the favorite
              button on a cause to have them appear here.
            </Text>
          </Stack>
        </Card>
      );
    }

    return userCauseSelections?.map((cause, i) => {
      // if the store has no products, don't render it
      if (stores[i].data?.meta.total === 0) return null;

      return (
        <MyFavoriteStoreCarouselCard
          key={cause.uuid}
          cause={cause}
          storeItemData={stores[i].data}
        />
      );
    });
  }, [userCauseSelections, stores]);

  return (
    <>
      {isLoading && (
        <Stack>
          <Skeleton h="82" />
          <Skeleton h="82" />
          <Skeleton h="82" />
          <Skeleton h="82" />
        </Stack>
      )}
      <Transition mounted={!isLoading} transition="fade" duration={500}>
        {(styles) => <Stack style={styles}>{causeCards}</Stack>}
      </Transition>
    </>
  );
};

const MyStoresPage = () => {
  const { isSmallScreen } = useBreakPoints();

  return (
    <Container pt="sm" pb="lg" px={isSmallScreen ? 'xs' : 'md'} size="xl">
      <Stack gap="xl">
        <MyStoresSearch />
        <Stack>
          <MyStoresHeader />
          <MyStores />
        </Stack>
      </Stack>
    </Container>
  );
};

export { MyStoresPage };
